import { useEffect } from "react";
import { Link, Outlet, useLocation, useNavigationType } from "react-router-dom";
import { TopNav } from "@/components/common/TopNav";
import { CookieConsent } from "@/components/common/CookieConsent";
import { useScrollPosition } from "@/hooks/useScrollPosition";
import { useBackground } from "@/contexts/BackgroundContext";
import { BackgroundSelector } from "@/components/common/BackgroundSelector";
import { OpArtBackground } from "@/components/main-page/OpArtBackground";
import { OpArtBackgroundSvg } from "@/components/main-page/OpArtBackgroundSvg";
import { WaveBackground } from "@/components/main-page/WaveBackground";
import { GridBackground } from "@/components/main-page/GridBackground";
import { InterferenceBackground } from "@/components/main-page/InterferenceBackground";
import type { BgStyle } from "@/components/common/BackgroundSelector";

const FADE_DURATION = 1500;

function BgLayer({
  style,
  scrollProgress,
  opacity,
}: {
  style: BgStyle;
  scrollProgress: number;
  opacity: number;
}) {
  return (
    <div
      style={{
        opacity,
        transition: `opacity ${FADE_DURATION}ms ease-in-out`,
        position: "fixed",
        inset: 0,
        zIndex: 0,
        pointerEvents: "none",
      }}
    >
      {style === "animated" && <OpArtBackground scrollProgress={scrollProgress} />}
      {style === "wave" && <WaveBackground scrollProgress={scrollProgress} />}
      {style === "grid" && <GridBackground scrollProgress={scrollProgress} />}
      {style === "interference" && <InterferenceBackground scrollProgress={scrollProgress} />}
      {style === "static" && <OpArtBackgroundSvg scrollProgress={scrollProgress} />}
    </div>
  );
}

/** Routes that should hide background layers and footer */
function isChromeHiddenRoute(pathname: string) {
  if (/^\/ir\/dataroom\/docs\/[^/]+/.test(pathname)) return true;
  if (pathname.startsWith("/demo/fund-compliance-agent")) return true;
  return false;
}

export function RootLayout() {
  const { scrollProgress } = useScrollPosition();
  const { layerA, layerB, activeLayer, activeBg, overlayOpacity, onBgChange } = useBackground();
  const location = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    if (navigationType === "PUSH" && !location.hash) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);

  const inViewer = isChromeHiddenRoute(location.pathname);
  const isExport = typeof window !== "undefined" && new URLSearchParams(location.search).get("mode") === "export";

  // Use the first path segment as transition key so sub-routes don't re-trigger
  const transitionKey = "/" + (location.pathname.split("/")[1] ?? "");

  // Export mode: render only the page content, no chrome
  if (isExport) {
    return <Outlet />;
  }

  return (
    <>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[300] focus:px-4 focus:py-2 focus:text-xs focus:font-[family-name:var(--font-mono)] focus:bg-[var(--color-fg)] focus:text-[var(--color-bg)] focus:rounded-full focus:outline-none"
      >
        Skip to content
      </a>
      {!inViewer && <BgLayer style={layerA} scrollProgress={scrollProgress} opacity={activeLayer === "a" ? 1 : 0} />}
      {!inViewer && <BgLayer style={layerB} scrollProgress={scrollProgress} opacity={activeLayer === "b" ? 1 : 0} />}
      <TopNav />
      <main id="main-content" key={transitionKey} className="page-transition">
        <Outlet />
      </main>
      {/* Hide footer when in document viewer */}
      {!inViewer && (
        <footer
          className="relative z-10 border-t border-[var(--color-border)] px-8 py-16 backdrop-blur-[2px]"
          style={{
            background: `rgba(var(--color-overlay-rgb),${overlayOpacity})`,
            transition: "background 1.5s ease-in-out",
          }}
        >
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
              <div>
                <h3 className="text-[10px] font-[family-name:var(--font-mono)] tracking-widest uppercase text-[var(--color-fg-tertiary)] mb-4">
                  Platform
                </h3>
                <ul className="space-y-2">
                  {[
                    { label: "Architecture", hash: "#architecture" },
                    { label: "Infrastructure", hash: "#infrastructure" },
                    { label: "Governance", hash: "#governance" },
                    { label: "Security", hash: "#security" },
                  ].map((item) => (
                    <li key={item.label}>
                      <Link to={`/platform${item.hash}`} className="text-xs text-[var(--color-fg-secondary)] hover:text-[var(--color-fg)] transition-colors">
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-[10px] font-[family-name:var(--font-mono)] tracking-widest uppercase text-[var(--color-fg-tertiary)] mb-4">
                  Products
                </h3>
                <ul className="space-y-2">
                  <li>
                    <a href="https://xeni.legal" target="_blank" rel="noopener noreferrer" className="text-xs text-[var(--color-fg-secondary)] hover:text-[var(--color-fg)] transition-colors">
                      XENI ↗
                    </a>
                  </li>
                  <li>
                    <Link to="/products#robolabx" className="text-xs text-[var(--color-fg-secondary)] hover:text-[var(--color-fg)] transition-colors">
                      Robolab X
                    </Link>
                  </li>
                  <li>
                    <Link to="/products#metaroom" className="text-xs text-[var(--color-fg-secondary)] hover:text-[var(--color-fg)] transition-colors">
                      Metaroom
                    </Link>
                  </li>
                  <li>
                    <Link to="/products#partner" className="text-xs text-[var(--color-fg-secondary)] hover:text-[var(--color-fg)] transition-colors">
                      Partner with us
                    </Link>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-[10px] font-[family-name:var(--font-mono)] tracking-widest uppercase text-[var(--color-fg-tertiary)] mb-4">
                  Company
                </h3>
                <ul className="space-y-2">
                  <li>
                    <Link to="/about" className="text-xs text-[var(--color-fg-secondary)] hover:text-[var(--color-fg)] transition-colors">
                      About
                    </Link>
                  </li>
                  <li>
                    <Link to="/insights" className="text-xs text-[var(--color-fg-secondary)] hover:text-[var(--color-fg)] transition-colors">
                      Insights
                    </Link>
                  </li>
                  <li>
                    <Link to="/about#careers" className="text-xs text-[var(--color-fg-secondary)] hover:text-[var(--color-fg)] transition-colors">
                      Careers
                    </Link>
                  </li>
                  <li>
                    <Link to="/ir" className="text-xs text-[var(--color-fg-secondary)] hover:text-[var(--color-fg)] transition-colors">
                      Investors
                    </Link>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-[10px] font-[family-name:var(--font-mono)] tracking-widest uppercase text-[var(--color-fg-tertiary)] mb-4">
                  Legal
                </h3>
                <ul className="space-y-2">
                  {[
                    { label: "Privacy policy", hash: "privacy" },
                    { label: "Terms of service", hash: "terms" },
                    { label: "Important disclosure", hash: "disclosure" },
                    { label: "Cookie policy", hash: "cookies" },
                  ].map((item) => (
                    <li key={item.label}>
                      <Link
                        to={`/legal#${item.hash}`}
                        onClick={() => window.scrollTo(0, 0)}
                        className="text-xs text-[var(--color-fg-secondary)] hover:text-[var(--color-fg)] transition-colors"
                      >
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-8 border-t border-[var(--color-border)]">
              <span className="text-xs text-[var(--color-fg-tertiary)] font-[family-name:var(--font-mono)] uppercase tracking-wider">
                &copy; {new Date().getFullYear()} COSINE X LTD. All rights reserved. COSX&reg; is a registered trade mark.
              </span>
              <div className="flex items-center gap-3">
                <a href="https://github.com/cosxai" target="_blank" rel="noopener noreferrer" className="text-[var(--color-fg-tertiary)] hover:text-[var(--color-fg-secondary)] transition-colors" aria-label="GitHub">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12Z"/></svg>
                </a>
                <a href="https://www.linkedin.com/company/cosxai" target="_blank" rel="noopener noreferrer" className="text-[var(--color-fg-tertiary)] hover:text-[var(--color-fg-secondary)] transition-colors" aria-label="LinkedIn">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286ZM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065Zm1.782 13.019H3.555V9h3.564v11.452ZM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003Z"/></svg>
                </a>
                <a href="https://x.com/cosxai" target="_blank" rel="noopener noreferrer" className="text-[var(--color-fg-tertiary)] hover:text-[var(--color-fg-secondary)] transition-colors" aria-label="X">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z"/></svg>
                </a>
                <BackgroundSelector value={activeBg} onChange={onBgChange} />
              </div>
            </div>
          </div>
        </footer>
      )}
      <CookieConsent />
    </>
  );
}
